import { useEffect, useRef, useState, useCallback } from "react";
import { Save, RotateCcw, Eye, Code2, Send, Copy } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

const TABS = [
  { key: "customer", label: "Customer thank-you", desc: "Sent to the person who submitted the contact form." },
  { key: "admin", label: "Admin lead notification", desc: "Sent to your lead-notification email when a new enquiry arrives." },
];

const PLACEHOLDERS = [
  { k: "name", d: "Customer name" },
  { k: "email", d: "Customer email" },
  { k: "phone", d: "Customer phone" },
  { k: "service", d: "Service they're enquiring about" },
  { k: "message", d: "Their message" },
  { k: "ref_id", d: "Short reference (e.g. ABC12345)" },
  { k: "date", d: "Submission date/time" },
];

export default function AdminTemplates() {
  useSeo({ title: "Email templates — RSR Admin" });
  const [active, setActive] = useState("customer");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [preview, setPreview] = useState("");
  const [view, setView] = useState("split"); // split | code | preview
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: d }] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/settings/defaults"),
      ]);
      setDefaults(d);
      setSubject(active === "customer" ? (s.customer_subject || d.customer_subject) : (s.admin_subject || d.admin_subject));
      setHtml(active === "customer" ? (s.customer_html || d.customer_html) : (s.admin_html || d.admin_html));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  // Live preview (debounced render via backend so placeholders resolve)
  useEffect(() => {
    if (!html) { setPreview(""); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/settings/preview", { html });
        setPreview(data.html || "");
      } catch (e) {
        setPreview(html); // fallback raw
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [html]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = active === "customer"
        ? { customer_subject: subject, customer_html: html }
        : { admin_subject: subject, admin_html: html };
      await api.put("/admin/settings", payload);
      toast.success("Template saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (!defaults) return;
    if (!confirm("Reset this template to the original default? Your unsaved changes will be lost.")) return;
    setSubject(active === "customer" ? defaults.customer_subject : defaults.admin_subject);
    setHtml(active === "customer" ? defaults.customer_html : defaults.admin_html);
    toast.success("Reset to default. Click Save to apply.");
  };

  const insertPlaceholder = (key) => {
    const placeholder = `{{${key}}}`;
    navigator.clipboard.writeText(placeholder).catch(() => {});
    setHtml(prev => prev + placeholder);
    toast.success(`Inserted ${placeholder} & copied to clipboard.`);
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12" data-testid="admin-templates">
      <p className="overline">Email templates</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tighter">Edit the emails.</h1>
      <p className="mt-2 text-sm text-muted-foreground">Visually edit the HTML for the customer thank-you and the admin lead-notification emails. Use the placeholder buttons to drop in dynamic values.</p>

      {/* Template tabs */}
      <div className="mt-8 flex border border-border w-fit" data-testid="template-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={`template-tab-${t.key}`}
            onClick={() => setActive(t.key)}
            className={`px-5 py-3 text-sm font-semibold ${active === t.key ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="mt-3 text-sm text-muted-foreground">{TABS.find(t => t.key === active)?.desc}</p>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground text-sm">Loading template…</div>
      ) : (
        <>
          {/* Subject */}
          <div className="mt-8 border border-border bg-card p-6">
            <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">Email subject</Label>
            <Input
              data-testid="template-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line"
              className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
            />
            <p className="mt-2 text-xs text-muted-foreground">Placeholders work here too — e.g. <code className="bg-secondary px-1.5 py-0.5">{"{{service}}"}</code></p>
          </div>

          {/* Placeholders */}
          <div className="mt-6 border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-display text-base font-bold tracking-tight">Available placeholders</h3>
              <span className="text-xs text-muted-foreground">Click to insert at the end + copy to clipboard.</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {PLACEHOLDERS.map(p => (
                <button
                  key={p.k}
                  data-testid={`placeholder-${p.k}`}
                  onClick={() => insertPlaceholder(p.k)}
                  title={p.d}
                  className="inline-flex items-center gap-2 border border-border hover:border-accent hover:text-accent px-3 py-2 text-xs font-mono font-semibold transition-colors"
                >
                  <Copy className="w-3 h-3" /> {`{{${p.k}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* View toggle */}
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <div className="flex border border-border">
              {[
                { k: "split", l: "Split" },
                { k: "code", l: "HTML" },
                { k: "preview", l: "Preview" },
              ].map(v => (
                <button
                  key={v.k}
                  data-testid={`view-${v.k}`}
                  onClick={() => setView(v.k)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold ${view === v.k ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}
                >
                  {v.k === "code" && <Code2 className="w-3 h-3 inline mr-1.5" />}
                  {v.k === "preview" && <Eye className="w-3 h-3 inline mr-1.5" />}
                  {v.l}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={resetToDefault}
                className="inline-flex items-center gap-2 border border-border hover:border-foreground/40 px-4 py-2 text-sm font-semibold"
              >
                <RotateCcw className="w-4 h-4" /> Reset to default
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="template-save"
                className="inline-flex items-center gap-2 bg-accent text-white px-5 py-2 text-sm font-semibold hover:bg-accent/90 disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          {/* Editor + Preview */}
          <div className={`mt-4 grid gap-4 ${view === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
            {(view === "split" || view === "code") && (
              <div className="border border-border bg-card flex flex-col">
                <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-2">
                  <Code2 className="w-3.5 h-3.5" /> HTML
                </div>
                <textarea
                  data-testid="template-editor"
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  spellCheck={false}
                  className="flex-1 min-h-[600px] bg-background border-0 p-4 font-mono text-xs leading-relaxed text-foreground focus:outline-none resize-y"
                />
              </div>
            )}
            {(view === "split" || view === "preview") && (
              <div className="border border-border bg-card flex flex-col">
                <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center justify-between">
                  <span className="flex items-center gap-2"><Eye className="w-3.5 h-3.5" /> Live preview (with sample data)</span>
                  <span className="text-[10px] normal-case tracking-normal font-normal">Renders on a real email client may vary.</span>
                </div>
                <iframe
                  data-testid="template-preview"
                  title="Email preview"
                  srcDoc={preview}
                  sandbox=""
                  className="flex-1 min-h-[600px] bg-white w-full"
                />
              </div>
            )}
          </div>

          <div className="mt-6 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <Send className="w-3.5 h-3.5" /> Want to send yourself a test? Use the "Send test email" panel on the <a href="/admin/settings" className="text-accent hover:underline">Settings</a> page.
          </div>
        </>
      )}
    </div>
  );
}
